import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { PageKey } from '../App';
import type { Session } from '../types/token';
import { useT } from '../i18n';
import LangSwitch from './LangSwitch';

interface NavProps {
  current: PageKey;
  onNavigate: (page: PageKey) => void;
  session: Session;
  onLogout: () => void;
}

interface NavItem {
  key: PageKey;
  labelKey: string;
  adminOnly?: boolean;
  icon?: ReactNode;
}

const HomeIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
    aria-hidden
  >
    <path d="m3 10 9-7 9 7v10a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z" />
  </svg>
);

const baseItems: NavItem[] = [
  { key: 'home', labelKey: 'nav.home', icon: HomeIcon },
  { key: 'works', labelKey: 'nav.works' },
  { key: 'about', labelKey: 'nav.about' },
  { key: 'console', labelKey: 'nav.console', adminOnly: true },
  { key: 'cocohome', labelKey: 'nav.cocohome', adminOnly: true },
  { key: 'travelMap', labelKey: 'nav.travelMap' },
  { key: 'health', labelKey: 'nav.health', adminOnly: true },
  { key: 'mochi', labelKey: 'nav.mochi', adminOnly: true },
];

export default function Nav({ current, onNavigate, session, onLogout }: NavProps) {
  const t = useT();
  const items = session.kind === 'guest'
    ? baseItems.filter((i) => i.key === 'about')
    : baseItems.filter((i) => !i.adminOnly || session.role === 'admin');
  const listRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef(new Map<PageKey, HTMLButtonElement>());
  const [activePill, setActivePill] = useState({ left: 0, width: 0, ready: false });

  useLayoutEffect(() => {
    const updateActivePill = () => {
      const list = listRef.current;
      const activeButton = itemRefs.current.get(current);
      if (!list || !activeButton) return;

      const buttonRect = activeButton.getBoundingClientRect();
      activeButton.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      setActivePill({
        left: activeButton.parentElement?.offsetLeft ?? activeButton.offsetLeft,
        width: buttonRect.width,
        ready: true,
      });
    };

    updateActivePill();
    window.addEventListener('resize', updateActivePill);
    return () => window.removeEventListener('resize', updateActivePill);
  }, [current, items.length]);

  return (
    <nav className="liquid-glass sticky top-3 z-20 mx-auto mt-3 flex w-[calc(100vw-1.5rem)] max-w-7xl flex-col items-stretch gap-3 rounded-[1.75rem] border border-white/45 bg-white/20 px-4 py-4 shadow-[0_18px_45px_rgb(15_23_42_/_0.08)] backdrop-blur-2xl backdrop-saturate-150 md:w-[calc(100vw-2.5rem)] md:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:px-8">
      <ul ref={listRef} className="relative flex max-w-full items-center gap-2 overflow-x-auto pb-1 text-sm font-mono xl:overflow-visible xl:pb-0">
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 hidden rounded-full bg-tan/25 blur-[2px] transition-[transform,width,opacity] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] xl:block"
          style={{
            width: activePill.width,
            opacity: activePill.ready ? 1 : 0,
            transform: `translateX(${activePill.left}px) scaleX(1.08)`,
          }}
        />
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 hidden rounded-full bg-tan shadow-[0_8px_24px_rgb(189_100_54_/_0.28),inset_0_1px_0_rgb(255_255_255_/_0.35)] transition-[transform,width,opacity] duration-450 ease-[cubic-bezier(0.22,1,0.36,1)] xl:block"
          style={{
            width: activePill.width,
            opacity: activePill.ready ? 1 : 0,
            transform: `translateX(${activePill.left}px)`,
          }}
        />
        {items.map((item) => {
          const isActive = current === item.key;
          return (
            <li key={item.key} className="relative z-10 flex items-center">
              <button
                ref={(node) => {
                  if (node) itemRefs.current.set(item.key, node);
                  else itemRefs.current.delete(item.key);
                }}
                type="button"
                onClick={() => onNavigate(item.key)}
                aria-label={t(item.labelKey)}
                title={t(item.labelKey)}
                className={[
                  'flex items-center whitespace-nowrap rounded-full px-3 py-1.5 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15',
                  isActive
                    ? 'bg-tan text-neutral-100 xl:bg-transparent'
                    : 'text-coffee hover:bg-white/35 hover:text-cocoa',
                ].join(' ')}
              >
                {item.icon ?? t(item.labelKey)}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex min-w-0 items-center justify-end gap-3 text-xs font-mono text-coffee/80">
        <LangSwitch variant="compact" />
        <span aria-hidden className="text-neutral-300">·</span>
        <span className="max-w-[12rem] truncate text-cocoa">
          {session.kind === 'guest' ? t('nav.tourSession') : session.company}
        </span>
        <span aria-hidden className="text-neutral-300">·</span>
        <button
          type="button"
          onClick={onLogout}
          className="rounded-full px-2 py-1 text-coffee transition hover:bg-neutral-100 hover:text-cocoa focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
        >
          {t('nav.logout')}
        </button>
      </div>
    </nav>
  );
}
